import React, { useEffect, PropsWithChildren, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { PaginationResponse } from "@/main/models/common/paginationResponse";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { PaginationRequest } from "@/main/models/common/paginationRequest";
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { PaginationLoading, PaginationNotFound } from "./paginationTools";
import * as S from "./styles";

export type IFetchMoreRequest<T = any> = T & {
	pageIndex?: number | undefined;
};
export interface IFetchMoreResponse {
	data: any[];
	totalNumberOfRecords: number;
}
export type ITypeGrid = "boxGrid" | "default";

interface IAcordeonHistoryProps<TData = any, TRequest extends PaginationRequest = PaginationRequest> {
	typeGrid?: ITypeGrid;
	dataGrid: TData[];
	formFilters: TRequest | null;
	idScrollableTarget?: string;
	showTitleResults?: boolean;
	showEndMessage?: boolean;
	fetchEqualFilters?: boolean;
	totalRecords?: number;
	sumTotalRecords?: number;
	setTotalRecords?: React.Dispatch<React.SetStateAction<number>>;
	fetchMore: (request: TRequest) => Promise<PaginationResponse<TData>>;
	setDataGrid: React.Dispatch<React.SetStateAction<TData[]>>;
	showResultNotFound?: boolean;
}

/**
 * Componente to handle with pagination
 *
 * @param dataGrid - Data to be rendered
 * @param formFilters - Filters to be applied
 * @param fetchMore - Function to fetch more data
 * @param setDataGrid - Function to set data
 * @param idScrollableTarget - Id of scrollable target
 * @param typeGrid - Type of grid
 * @param showTitleResults - Show title results
 * @param showEndMessage - Show message when all results where loaded
 * @param fetchEqualFilters - if false, fetchMore will be called only if formFilters is different from the previous one
 * @param totalRecords - Total records (optional)
 * @param setTotalRecords - Function to set total records (optional)
 * @param sumTotalRecords - Function to add total records (optional)
 * @param children - Children to be rendered
 * @param showResultNotFound - if false, doesn't show result not found image
 *
 * @example
 * const [data, setData] = useState<any[]>([]);
 * const [formFilters, setFormFilters] = useState<PaginationRequest | null>(null);
 *
 * async function fetchData(request: PaginationRequest) {
 *  return anyAPI.get(request).then(response => response.data);
 * }
 *
 * function getData() {
 *  const request: PaginationRequest = {
 *    pageIndex: PaginationEnum.PageIndexIntial,
 *    pageSize: PaginationEnum.PageSize10,
 *   };
 *  setFormFilters(request);
 *  setData([]);
 * }
 *
 * useEffect(() => {
 *  getData();
 * }, []);
 *
 * <KOGPaginationGrid
 *   dataGrid={data}
 *   formFilters={formFilters}
 *   fetchMore={fetchData}
 *   setDataGrid={setData}
 * >
 *   {data.map(file => (
 *     ...
 *   ))
 *   }
 * </KOGPaginationGrid>
 *
 */

function KOGPaginationGrid<TData = any, TRequest extends PaginationRequest = PaginationRequest>({
	typeGrid = "default",
	dataGrid,
	formFilters,
	children,
	idScrollableTarget,
	showTitleResults = false,
	showEndMessage = true,
	fetchEqualFilters = false,
	totalRecords: totalRecordsProp,
	sumTotalRecords = 0,
	setTotalRecords: setTotalRecordsProp,
	fetchMore,
	setDataGrid,
	showResultNotFound = true,
}: PropsWithChildren<IAcordeonHistoryProps<TData, TRequest>>) {
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const localTotalRecordsState = useState(0);
	const [totalRecords, setTotalRecords] =
		isNullUndefined(totalRecordsProp) || isNullUndefined(setTotalRecordsProp)
			? localTotalRecordsState
			: [totalRecordsProp, setTotalRecordsProp];
	const dependecyUseEffect = fetchEqualFilters ? formFilters : JSON.stringify(formFilters);

	async function onLoadInfiniteScroll(isInitialLoad: boolean = false) {
		const nextIndex = isInitialLoad ? PaginationEnum.PageIndexIntial : pageIndex + 1;

		const mountRequest = Object.assign({}, formFilters, {
			pageIndex: nextIndex,
		});
		setPageIndex(nextIndex);

		await fetchMore(mountRequest).then((dataFetch) => {
			const { results = [], totalNumberOfRecords = 0, totalNumberOfPages = 0 } = dataFetch;

			if (totalNumberOfPages === 0 || totalNumberOfRecords === 0) {
				setHasShowMore(false);
				setIsLoading(false);
				return;
			}

			const newData = [...dataGrid, ...results];
			if (totalRecords != totalNumberOfRecords) setTotalRecords(totalNumberOfRecords);
			setDataGrid(newData);
		});
	}

	useEffect(() => {
		if (isNullUndefined(formFilters)) return;
		setIsLoading(true);
		onLoadInfiniteScroll(true).finally(() => setIsLoading(false));
	}, [dependecyUseEffect]);

	useEffect(() => {
		setHasShowMore(totalRecords > dataGrid.length);
	}, [totalRecords, dataGrid]);

	const RenderLoading = () => <PaginationLoading typeGrid={typeGrid} />;
	if (dataGrid.length === 0 && formFilters === null) return null;
	if (isLoading) return <RenderLoading />;
	if (formFilters !== null && dataGrid.length === 0)
		return showResultNotFound ? <PaginationNotFound typeGrid={typeGrid} /> : <></>;

	const titleBoxGrid = getDefaultMessageResultsFound(dataGrid.length, sumTotalRecords + totalRecords);
	return (
		<div style={{ height: idScrollableTarget ? "auto" : "95vh" }}>
			<InfiniteScroll
				scrollableTarget={idScrollableTarget || null}
				hasMore={hasShowMore}
				dataLength={dataGrid.length}
				next={onLoadInfiniteScroll}
				loader={<RenderLoading />}
				endMessage={
					showEndMessage && (
						<S.EndMessage className="d-flex d-flex justify-content-center">
							{getTranslateByKey("information.allResultsWereDisplayed")}
						</S.EndMessage>
					)
				}
			>
				{showTitleResults && <S.TitleResults>{titleBoxGrid}</S.TitleResults>}
				{children}
			</InfiniteScroll>
		</div>
	);
}

export default KOGPaginationGrid;
